import { useState } from 'react'
import { useCV } from '../hooks/useCV'
import { parsePeriod } from '../utils/period'

interface Bar {
  key: string
  label: string
  fullLabel: string
  detail: string
  start: number
  end: number
  track: 'work' | 'study'
}

const Timeline = () => {
  const { experiences, education } = useCV()
  const [active, setActive] = useState<Bar | null>(null)

  const bars: Bar[] = [
    ...experiences.map((e) => ({
      key: `w-${e.company}`,
      fullLabel: e.company.replace(/ Co\., Ltd| Group| Solutions/g, ''),
      label: e.company.replace(/ Co\., Ltd| Group| Software Solutions| Software/g, ''),
      detail: `${e.title} at ${e.company}, ${e.period}`,
      track: 'work' as const,
      ...parsePeriod(e.period),
    })),
    ...education.map((e) => ({
      key: `s-${e.school}`,
      fullLabel: e.school.replace(' University HCMC', '').replace(' College', ''),
      label: e.school.replace(' University HCMC', '').replace(' College', ''),
      detail: `${e.major} at ${e.school}, ${e.period}`,
      track: 'study' as const,
      ...parsePeriod(e.period),
    })),
  ].filter((b) => Number.isFinite(b.start) && Number.isFinite(b.end))

  const min = Math.floor(Math.min(...bars.map((b) => b.start)))
  const max = Math.ceil(Math.max(...bars.map((b) => b.end)))
  const span = max - min
  const years = Array.from({ length: span + 1 }, (_, i) => min + i)
  const pct = (v: number) => `${((v - min) / span) * 100}%`

  const renderTrack = (track: Bar['track'], name: string) => (
    <div className="grid grid-cols-1 sm:grid-cols-[4.5rem_1fr] sm:items-center gap-x-3">
      <span className="text-[0.6875rem] sm:text-sm text-mute">{name}</span>
      <div className={`relative border-b border-rule ${track === 'work' ? 'h-12' : 'h-9'}`}>
        {bars
          .filter((b) => b.track === track)
          .map((b) => {
            // Bars too narrow for text get their label above the bar instead
            const narrow = (b.end - b.start) / span < 0.07
            return (
            <span key={b.key}>
              {narrow && (
                <span
                  aria-hidden
                  style={{ left: pct(b.start) }}
                  className="absolute top-0 text-[0.6875rem] leading-4 text-mute whitespace-nowrap"
                >
                  {b.fullLabel}
                </span>
              )}
            <button
              type="button"
              aria-label={b.detail}
              onMouseEnter={() => setActive(b)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(b)}
              onBlur={() => setActive(null)}
              style={{ left: pct(b.start), width: pct(b.end - b.start + min) }}
              className={`absolute bottom-1 min-w-[8px] ${track === 'work' ? 'top-5' : 'top-1'} overflow-hidden text-ellipsis whitespace-nowrap px-0.5 sm:px-1.5 text-left text-[0.625rem] sm:text-xs font-medium transition-opacity ${
                track === 'work' ? 'bg-accent text-paper' : 'bg-rule text-ink'
              } ${active && active.key !== b.key ? 'opacity-40' : 'opacity-100'}`}
            >
              {narrow ? '' : b.label}
            </button>
            </span>
            )
          })}
      </div>
    </div>
  )

  return (
    <div className="mt-16 md:mt-20">
      <div className="space-y-3 sm:space-y-2">
        {renderTrack('work', 'Work')}
        {renderTrack('study', 'Study')}
        <div className="grid grid-cols-1 sm:grid-cols-[4.5rem_1fr] gap-x-3" aria-hidden>
          <span className="hidden sm:block" />
          <div className="relative h-4 text-[0.6875rem] text-mute">
            {years.map((y) => (
              <span
                key={y}
                style={{ left: pct(y) }}
                className={`absolute -translate-x-1/2 ${(y - min) % 2 ? 'hidden sm:block' : ''}`}
              >
                {y}
              </span>
            ))}
          </div>
        </div>
      </div>
      <p className="mt-3 min-h-[1.5rem] text-sm text-mute" aria-live="polite">
        {active ? active.detail : 'Work and study since 2016. Hover or tap a bar for details.'}
      </p>
    </div>
  )
}

export default Timeline
